import { useRef, useState } from 'react';
import { content } from '../data/content';
import { SectionHeading } from './ui/Ui';

/**
 * تبسيط الاستخدام إلى ثلاث خطوات يزيل اعتراض «شكله معقّد» —
 * من أكبر أسباب التردد في الأجهزة المنزلية.
 *
 * والفيديو يثبت الخطوات بدل أن يَعِد بها: الرؤية تُقنع أسرع من ثلاث فقرات.
 */
export default function HowItWorks() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  /**
   * الفيديو لا يُحمَّل مع الصفحة إطلاقاً.
   *
   * preload="none" يعني أن المتصفح لا ينزّل منه بايتاً واحداً عند الفتح — يعرض
   * صورة الغلاف فقط (42KB) — ولا يبدأ التنزيل إلا عند الضغط. لذلك لا يزيد
   * الفيديو زمن فتح الصفحة ولو بمقدار واحد، مهما كبر.
   *
   * ويعمل بالصوت لأن التشغيل يبدأ بضغطة من الزائر: المتصفحات تحجب الصوت في
   * التشغيل التلقائي وحده، وتسمح به متى جاء استجابةً لتفاعل مباشر.
   */
  const play = () => {
    const video = videoRef.current;
    if (!video) return;

    setPlaying(true);
    video.play();
  };

  return (
    <section id="how" className="scroll-mt-16 md:scroll-mt-20 py-10 md:py-20 bg-white">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow={content.howEyebrow}
          icon="fa-wand-magic-sparkles"
          title={content.howTitle}
          highlight={content.howTitleHighlight}
        />

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 max-w-5xl mx-auto items-center">
          {/* ===== الفيديو ===== */}
          <div className="lg:col-span-2">
            <div className="relative mx-auto max-w-[260px] sm:max-w-[300px] lg:max-w-none rounded-3xl overflow-hidden bg-z-navy shadow-xl shadow-z-navy/20">
              <video
                ref={videoRef}
                src="/how-it-works.mp4"
                poster="/how-it-works-poster.jpg"
                preload="none"
                playsInline
                controls={playing}
                onPlay={() => setPlaying(true)}
                className="w-full aspect-[9/16] object-cover"
              />

              {/* بلا طبقة داكنة فوق الصورة: كانت تخفّض تشبّع الألوان فتبدو صورة
                  الغلاف باهتة قبل التشغيل. التباين يأتي من القرص الأبيض المصمت
                  ومن خلفية السطر وحدهما، فتظهر الصورة بألوانها الكاملة.
                  والغطاء يختفي بعد أول تشغيل فتظهر أدوات التحكّم بلا تعارض */}
              {!playing && (
                <button
                  onClick={play}
                  aria-label="تشغيل الفيديو"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 transition"
                >
                  <span className="w-16 h-16 rounded-full bg-white text-z-navy flex items-center justify-center text-xl shadow-2xl shadow-z-navy/40">
                    {/* الإزاحة تُوسّط المثلث بصرياً — المثلث المتساوي يبدو مزاحاً لليسار داخل دائرة */}
                    <i className="fa-solid fa-play ms-1" />
                  </span>
                  {/* تنبيه الصوت مقصود: الضغط على زر تشغيل صامت مألوف، وصوتٌ
                      مفاجئ في مكان عام يجعل الزائر يغلق الصفحة لا الفيديو */}
                  <span className="text-white text-xs font-bold bg-z-navy/85 px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg">
                    <i className="fa-solid fa-volume-high" /> شوفه وهو بيشتغل
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* ===== الخطوات ===== */}
          <ol className="lg:col-span-3 space-y-3 md:space-y-4">
            {content.howSteps.map((step) => (
              <li key={step.num} className="dash-card bg-white flex gap-4 p-4 md:p-5">
                <span className="w-11 h-11 shrink-0 rounded-2xl bg-z-navy text-white flex items-center justify-center text-lg font-serif font-bold">
                  {step.num}
                </span>
                <div className="min-w-0">
                  <h3 className="text-base md:text-lg font-bold mb-1 text-z-navy leading-snug">{step.title}</h3>
                  <p className="text-z-gray text-[13px] md:text-sm leading-relaxed">{step.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
